import { Button } from '@ctw/shared/components/button';
import { ContentError } from '@ctw/shared/components/errors/content-error';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { Heading } from '@ctw/shared/components/heading';
import { HorizontalDivider } from '@ctw/shared/components/horizontal-divider';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { PatientSummarizationUsageTicker } from '@ctw/shared/content/summarization/patient-summarization-usage-ticker';
import {
  PatientSummarizationInsufficientDataBanner,
  PatientSummarizationLimitApproachingBanner,
  PatientSummarizationLimitReachedBanner,
} from '@ctw/shared/content/summarization/patient-summarization-warning-banners';
import { PatientSummaryCard } from '@ctw/shared/content/summarization/patient-summary-card';
import {
  PatientSummaryInsufficientData,
  PatientSummaryLimitReached,
} from '@ctw/shared/content/summarization/patient-summary-state-panels';
import { usePatientSummaries } from '@ctw/shared/content/summarization/use-patient-summaries';
import { usePatientSummaryFeedbackDrawer } from '@ctw/shared/content/summarization/use-patient-summary-feedback-drawer';
import { useUserPatientSummarizationContext } from '@ctw/shared/content/summarization/user-patient-summarization-context';
import { SupportFormLink } from '@ctw/shared/content/support-form-link';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { formatDateToLocalString } from '@ctw/shared/utils/dates';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

export interface PatientSummaryProps {
  className?: ClassName;
}

interface PatientSummaryLayoutProps {
  className?: ClassName;
  summaryResponse: ReturnType<typeof usePatientSummaries>;
  children: ReactNode;
}

const PatientSummaryLayout = ({
  className,
  summaryResponse,
  children,
}: PatientSummaryLayoutProps) => {
  const { patient } = usePatientContext();
  const { openPatientSummaryFeedbackDrawer } = usePatientSummaryFeedbackDrawer({
    summaryResponse,
  });

  return (
    <div className={twx(className, 'p-3')}>
      <header className={tw`flex items-center justify-between`}>
        <div className={tw`flex flex-col gap-2`}>
          <Heading level="h1" className={tw`text-content-main`}>
            Your 5-Minute Briefing On {patient.fullName}
          </Heading>
          {summaryResponse.lastRefreshed && (
            <span className="text-content-subtle text-sm">
              Last refreshed: {formatDateToLocalString(summaryResponse.lastRefreshed)}
            </span>
          )}
        </div>
        <div className={tw`flex flex-col items-end justify-end gap-2`}>
          <PatientSummarizationUsageTicker showHoverDetail={true} />
          <div className={tw`flex items-center justify-end gap-1`}>
            <Button
              type="button"
              variant="secondary"
              size="xs"
              className={tw`flex items-center justify-center border-0 px-2 shadow-none`}
              ariaLabel="Thumbs Up"
              onClick={() =>
                openPatientSummaryFeedbackDrawer({ initialFeedbackPolarity: 'positive' })
              }
            >
              <FontAwesomeIcon icon={faThumbsUp} className={tw`h-3.5 w-3.5 text-content-subtle`} />
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="xs"
              className={tw`flex items-center justify-center border-0 px-2 shadow-none`}
              ariaLabel="Thumbs Down"
              onClick={() =>
                openPatientSummaryFeedbackDrawer({ initialFeedbackPolarity: 'negative' })
              }
            >
              <FontAwesomeIcon
                icon={faThumbsDown}
                className={tw`h-3.5 w-3.5 text-content-subtle`}
              />
            </Button>
          </div>
        </div>
      </header>
      <HorizontalDivider thickness="thin" />
      {children}
    </div>
  );
};

export const PatientSummary = withErrorBoundary({
  boundaryName: 'PatientSummary',
  Component: ({ className }: PatientSummaryProps) => {
    const telemetry = useTelemetry();
    const { patient } = usePatientContext();
    const { summarizationLimitApproaching, summarizationLimitReached } =
      useUserPatientSummarizationContext();
    const summaryResponse = usePatientSummaries({ patientUpid: patient.UPID });

    const [presentedSummaryCount, setPresentedSummaryCount] = useState(0);

    // Memoize the callback to prevent new function reference on each render
    const handleAllContentPresented = useCallback(() => {
      setPresentedSummaryCount((prev) => prev + 1);
    }, []);

    // Kick off presenting summaries once the first is available. The presented count will later be
    // incremented as each summary finishes streaming
    useEffect(() => {
      telemetry.trackInteractionWithCondition('patient_summary_viewed', 'once_per_page_view', {
        summaryJobId: summaryResponse.summaryJobId,
        summaryJobStatus: summaryResponse.status,
      });

      for (let summaryIndex = 0; summaryIndex < presentedSummaryCount; summaryIndex++) {
        telemetry.trackInteractionWithCondition(
          'patient_summary_card_viewed',
          'once_per_page_view',
          {
            summaryJobId: summaryResponse.summaryJobId,
            summaryJobStatus: summaryResponse.status,
            summaryStatus: summaryResponse.summaries[summaryIndex]?.status,
            summaryIndex,
          },
          { summaryIndex },
        );
      }

      if (summaryResponse.summaries.length > 0 && presentedSummaryCount === 0) {
        setPresentedSummaryCount(1);
      }
    }, [telemetry, summaryResponse, presentedSummaryCount]);

    useEffect(() => {
      if (summaryResponse.status === 'insufficient-data') {
        telemetry.trackInteractionWithCondition(
          'patient_summary_insufficient_data',
          'once_per_page_view',
        );
      }
    }, [telemetry, summaryResponse.status]);

    useEffect(() => {
      if (summaryResponse.status === 'limit-reached') {
        telemetry.trackInteractionWithCondition(
          'patient_summary_limit_reached_no_cache',
          'once_per_page_view',
        );
      }
    }, [telemetry, summaryResponse.status]);

    if (summaryResponse.status === 'insufficient-data') {
      return (
        <PatientSummaryLayout className={className} summaryResponse={summaryResponse}>
          <PatientSummarizationInsufficientDataBanner />
          <PatientSummaryInsufficientData />
        </PatientSummaryLayout>
      );
    }

    if (summaryResponse.status === 'limit-reached') {
      return (
        <PatientSummaryLayout className={className} summaryResponse={summaryResponse}>
          <PatientSummarizationLimitReachedBanner
            variant="standalone"
            dismissable={false}
            summaryJobId={summaryResponse.summaryJobId}
            summaryJobStatus={summaryResponse.status}
          />
          <PatientSummaryLimitReached />
        </PatientSummaryLayout>
      );
    }

    if (summaryResponse.status === 'error') {
      return (
        <div>
          <ContentError title="A summary is not available for this patient.">
            <span>If this problem persists </span>
            <SupportFormLink buttonText="contact support" className={tw`link text-base`} />.
          </ContentError>
        </div>
      );
    }

    if (summaryResponse.status === 'running' && summaryResponse.summaries.length === 0) {
      return <LoadingSpinner centered={true} message="Loading Summary" />;
    }

    if (summaryResponse.summaries.length === 0) {
      return (
        <ContentError title="No summaries are available for this patient">
          <span>If this is unexpected, please </span>
          <SupportFormLink buttonText="contact support" className={tw`link text-base`} />.
        </ContentError>
      );
    }

    return (
      <PatientSummaryLayout className={className} summaryResponse={summaryResponse}>
        {summarizationLimitReached ? (
          <PatientSummarizationLimitReachedBanner
            variant="standalone"
            summaryJobId={summaryResponse.summaryJobId}
            summaryJobStatus={summaryResponse.status}
          />
        ) : summarizationLimitApproaching ? (
          <PatientSummarizationLimitApproachingBanner
            variant="standalone"
            summaryJobId={summaryResponse.summaryJobId}
            summaryJobStatus={summaryResponse.status}
          />
        ) : null}
        <div className={tw`flex flex-col gap-3`}>
          {summaryResponse.summaries.map((summary, index) => (
            <PatientSummaryCard
              key={`summary-card-${summary.title}`}
              summaryResponse={summaryResponse}
              summaryIndex={index}
              enabled={presentedSummaryCount >= index + 1}
              onAllContentPresented={handleAllContentPresented}
            />
          ))}
        </div>
        <div className={tw`pt-6 text-xs italic`}>
          This health record summary is generated using AI technology and is intended for
          informational purposes only. While efforts have been made to ensure accuracy, the
          information provided may not capture all relevant details or updates.
        </div>
      </PatientSummaryLayout>
    );
  },
});
